import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";

type BillingState =
  | { status: "loading" }
  | {
      status: "ready";
      grandfathered: boolean;
      hasActive: boolean;
      inGrace: boolean;
      tier: string | null;
      subscription: any | null;
    };

export function useSubscription() {
  const { user } = useAuth();
  const [state, setState] = useState<BillingState>({ status: "loading" });

  const refetch = async () => {
    if (!user) {
      setState({ status: "loading" });
      return;
    }
    const env = getStripeEnvironment();

    const [{ data: profile }, { data: sub }] = await Promise.all([
      supabase
        .from("profiles")
        .select("billing_grandfathered, subscription_tier")
        .eq("id", user.id)
        .maybeSingle(),
      supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const grandfathered = !!profile?.billing_grandfathered;
    const tier = profile?.subscription_tier ?? null;

    let hasActive = false;
    let inGrace = false;
    if (sub) {
      const periodEnd = sub.current_period_end ? new Date(sub.current_period_end as string) : null;
      const now = new Date();
      // 'unpaid' = Stripe exhausted retries → access is revoked, no grace.
      // 'incomplete_expired' / 'incomplete' = initial payment never landed → no access.
      if (sub.status === "unpaid" || sub.status === "incomplete_expired" || sub.status === "incomplete") {
        hasActive = false;
        inGrace = false;
      } else if (
        (sub.status === "active" || sub.status === "trialing") &&
        (!periodEnd || periodEnd > now)
      ) {
        hasActive = true;
      } else if (sub.status === "canceled" && periodEnd && periodEnd > now) {
        hasActive = true;
      } else if (sub.status === "past_due" && periodEnd) {
        const grace = new Date(periodEnd);
        grace.setDate(grace.getDate() + 5);
        if (grace > now) inGrace = true;
      }
    }

    setState({
      status: "ready",
      grandfathered,
      hasActive,
      inGrace,
      tier,
      subscription: sub,
    });
  };

  useEffect(() => {
    refetch();
    if (!user) return;
    const channel = supabase
      .channel(`subs-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => refetch()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const hasAccess =
    state.status === "ready" && (state.grandfathered || state.hasActive || state.inGrace);

  return { ...state, hasAccess, refetch };
}
